import { Wifi, HardDrive } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * Categorical pill showing whether a camera ingests via HTTP (modern DeterCam)
 * or FTP (legacy). Matches the existing "Shared" badge pattern in CamerasPage.tsx
 * (rounded-full, px-1.5 py-0.5, text-[10px], small icon, border).
 *
 * Color tokens: blue tint for HTTP (newer/heartbeat-capable), neutral muted for
 * FTP (legacy). Uses Tailwind utilities that resolve correctly in both light
 * and dark themes.
 */
interface IngestionTypeBadgeProps {
  type: 'http' | 'ftp'
  className?: string
  /**
   * When true, use high-contrast translucent styling readable on any photo
   * background (for thumbnail overlays). When false (default), use the
   * card-friendly tinted style that matches the rest of the dashboard.
   */
  overlay?: boolean
}

export function IngestionTypeBadge({ type, className, overlay = false }: IngestionTypeBadgeProps) {
  const { t } = useTranslation('cameras')
  const isHttp = type === 'http'
  const Icon = isHttp ? Wifi : HardDrive

  let toneClasses: string
  if (overlay) {
    toneClasses = isHttp
      ? 'bg-blue-500/80 text-white border-blue-300/40 backdrop-blur-sm shadow-sm'
      : 'bg-black/50 text-white border-white/20 backdrop-blur-sm shadow-sm'
  } else {
    toneClasses = isHttp
      ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900'
      : 'bg-muted/50 text-muted-foreground border-border'
  }

  return (
    <Tooltip content={isHttp ? t('badge.httpTooltip') : t('badge.ftpTooltip')}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border shrink-0',
          toneClasses,
          className,
        )}
      >
        <Icon className="h-2.5 w-2.5" />
        {isHttp ? t('badge.http') : t('badge.ftp')}
      </span>
    </Tooltip>
  )
}
